import { create } from 'zustand';

interface UIState {
  // Sidebar state
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Theme
  darkMode: boolean;

  // Loading states
  globalLoading: boolean;

  // Modal states
  activeModal: string | null;
  modalData: unknown;

  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleDarkMode: () => void;
  setGlobalLoading: (loading: boolean) => void;
  openModal: (modalId: string, data?: unknown) => void;
  closeModal: () => void;
}

// On a phone/narrow tablet, the sidebar renders as a full-screen drawer (see Sidebar.tsx) -
// defaulting it open would greet a first-time mobile visitor with their whole dashboard hidden
// behind an overlay. Matches Sidebar/Header's own `lg` (1024px) mobile cutoff. Evaluated once at
// module load (before test setup's matchMedia mock or any SSR DOM exists), so this falls back to
// the desktop-safe default (open) unless a real matchMedia is actually available to ask.
const defaultSidebarOpen =
  typeof window === 'undefined' ||
  typeof window.matchMedia !== 'function' ||
  window.matchMedia('(min-width: 1024px)').matches;

export const useUIStore = create<UIState>((set) => ({
  // Initial state
  sidebarOpen: defaultSidebarOpen,
  sidebarCollapsed: false,
  darkMode: false,
  globalLoading: false,
  activeModal: null,
  modalData: null,

  // Actions
  toggleSidebar: () =>
    set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

  toggleDarkMode: () =>
    set((state) => ({ darkMode: !state.darkMode })),

  setGlobalLoading: (loading) => set({ globalLoading: loading }),

  openModal: (modalId, data) =>
    set({ activeModal: modalId, modalData: data }),

  closeModal: () => set({ activeModal: null, modalData: null }),
}));

// Modal IDs
export const MODALS = {
  CREATE_TICKET: 'create-ticket',
  CREATE_CUSTOMER: 'create-customer',
  ADD_NOTE: 'add-note',
  ADD_PART: 'add-part',
  CONFIRM_DELETE: 'confirm-delete',
  AI_DIAGNOSIS: 'ai-diagnosis',
  PAYMENT: 'payment',
} as const;
